import { useRef, useState } from "react"

import { FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"

/** The shortest segment the fields will produce. */
const MIN_SEGMENT_SECONDS = 0.1

/** A bound as the field shows it: to the millisecond, the precision a region keeps. */
function displaySeconds(seconds: number): string {
  return String(Math.round(seconds * 1000) / 1000)
}

function TimeField({
  label,
  max,
  min,
  onCommit,
  value,
}: {
  label: string
  max: number
  min: number
  onCommit: (seconds: number) => void
  value: number
}) {
  const [draft, setDraft] = useState(displaySeconds(value))
  // Escape moves focus out of the field, and the blur that follows must not
  // save the edit Escape abandoned.
  const discardOnBlurRef = useRef(false)
  // A drag or another field can move this bound; the draft follows it.
  const [draftFor, setDraftFor] = useState(value)
  if (draftFor !== value) {
    setDraftFor(value)
    setDraft(displaySeconds(value))
  }

  const commit = () => {
    if (discardOnBlurRef.current) {
      discardOnBlurRef.current = false
      return
    }
    // An untouched field shows a rounded value; leave the stored one as it is.
    if (draft === displaySeconds(value)) {
      return
    }
    const parsed = Number.parseFloat(draft)
    if (!Number.isFinite(parsed)) {
      setDraft(displaySeconds(value))
      return
    }
    const next = Math.max(min, Math.min(max, parsed))
    setDraft(displaySeconds(next))
    if (next !== value) {
      onCommit(next)
    }
  }

  return (
    <FormField label={label} className="w-32">
      {(fieldProps) => (
        <Input
          {...fieldProps}
          type="number"
          inputMode="decimal"
          step={MIN_SEGMENT_SECONDS}
          min={displaySeconds(min)}
          max={displaySeconds(max)}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              commit()
            } else if (event.key === "Escape") {
              if (draft !== displaySeconds(value)) {
                event.preventDefault()
              }
              discardOnBlurRef.current = true
              setDraft(displaySeconds(value))
            }
          }}
        />
      )}
    </FormField>
  )
}

/**
 * Sets a selected segment's start and end by typing, the alternative to
 * dragging its edges. A zero-length marker has one time, which moves it.
 * Values commit on Enter or when focus leaves the field, clamped so the
 * segment stays inside the recording and keeps a positive length. Escape
 * discards the edit.
 */
export function AudioRegionBoundsFields({
  duration,
  end,
  onChange,
  start,
}: {
  duration: number
  end: number
  onChange: (start: number, end: number) => void
  start: number
}) {
  if (end <= start) {
    return (
      <TimeField
        label="Time (seconds)"
        min={0}
        max={duration}
        value={start}
        onCommit={(time) => onChange(time, time)}
      />
    )
  }
  return (
    <div className="flex items-end gap-2">
      <TimeField
        label="Start (seconds)"
        min={0}
        max={Math.max(0, end - MIN_SEGMENT_SECONDS)}
        value={start}
        onCommit={(nextStart) => onChange(nextStart, end)}
      />
      <TimeField
        label="End (seconds)"
        min={Math.min(duration, start + MIN_SEGMENT_SECONDS)}
        max={duration}
        value={end}
        onCommit={(nextEnd) => onChange(start, nextEnd)}
      />
    </div>
  )
}
